/**
 * CE-1 — never silently drop a selected customer-send attachment (client).
 *
 * The office can select files to ride on a customer email, but some categories
 * are never customer-sendable (internal cheat sheets / store stamps / raw
 * uploads), signatures are privacy-restricted, and a stored file can fail to
 * read at send time. The send-preview endpoint reports which SELECTED files
 * will NOT be attached (each with a plain-English reason), and approve-and-send
 * refuses with 409 DROPPED_ATTACHMENTS_UNCONFIRMED unless the request carries
 * `confirmDroppedAttachments: true`.
 *
 * This panel is the operator-facing surface for both signals: it lists every
 * file that won't ride and why, BEFORE anything is sent, and offers exactly two
 * choices — send without those files ("Send anyway", which posts the confirm
 * flag) or back out ("Cancel"). It renders identically whether the drop list
 * came from the preview or from a 409 response, so a dropped attachment can
 * never surface as a raw error.
 */
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Why a selected attachment will not ride on the customer email. Mirrors the
 * server `DropReason` union in customerSendDroppedAttachments.ts. CE-2 made
 * explicitly-selected internal docs sendable, so the server no longer emits
 * `non_sendable_category`; it stays here so old audit payloads / a not-yet-
 * redeployed server still render a sane reason. */
export type DropReason =
    | "non_sendable_category"
    | "privacy_restricted"
    | "download_failed"
    | "over_size_cap"
    | "render_failed"
    | "pdf_failed_check";

/** A selected attachment that will not be attached, with the reason. Mirrors
 * the server `DroppedAttachment` shape returned by send-preview / the 409. */
export interface DroppedAttachment {
    id: string;
    fileName: string;
    reason: DropReason;
    /** Human sentence from the server — shown verbatim when present. */
    detail: string;
}

/** Fallback plain-English reason if the server ever omits `detail`. */
const REASON_FALLBACK: Record<DropReason, string> = {
    non_sendable_category:
        "internal document — not a customer-facing file (cheat sheets, store stamps, and raw uploads never ride on a customer email)",
    privacy_restricted: "signature capture — privacy-restricted, never sent to customers",
    download_failed: "the stored file could not be read at send time",
    render_failed: "the PDF could not be generated at send time",
    over_size_cap: "too large to fit — the email attachment payload is capped, and this file does not fit",
    pdf_failed_check:
        "this PDF will not open in Adobe or Outlook, or its answers do not show on the page — fix it with Rebuild reports on the City Filing page",
};

function reasonText(d: DroppedAttachment): string {
    return d.detail?.trim() || REASON_FALLBACK[d.reason] || "this file will not be attached";
}

/**
 * Warning panel + Send-anyway / Cancel footer. Designed to sit inside a Dialog
 * (mirrors SendConfirmSummary: content fragment + right-aligned button row).
 */
export function DroppedAttachmentsWarning({
    dropped,
    isPending,
    onSendAnyway,
    onCancel,
}: {
    dropped: DroppedAttachment[];
    /** Disables both buttons while the send is in flight. */
    isPending?: boolean;
    /** Send without the dropped files — the caller posts confirmDroppedAttachments. */
    onSendAnyway: () => void;
    /** Back out without sending. */
    onCancel: () => void;
}) {
    const n = dropped.length;

    return (
        <>
            <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="space-y-0.5">
                        <p className="font-medium">
                            {n} file{n === 1 ? "" : "s"} won&apos;t be attached
                        </p>
                        <p className="text-xs text-amber-800/90 dark:text-amber-200/90">
                            These documents will not be sent to the customer. Review them, then send without them or
                            cancel to make changes.
                        </p>
                    </div>
                </div>

                <ul className="space-y-1.5" aria-label="Files that won't be attached">
                    {dropped.map((d) => (
                        <li key={d.id} className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                            <div className="break-all font-medium">{d.fileName}</div>
                            <div className="text-xs text-foreground/60">{reasonText(d)}</div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="flex justify-end gap-2 pt-3">
                <Button variant="outline" onClick={onCancel} disabled={isPending}>
                    Cancel
                </Button>
                <Button
                    className="bg-green-700 hover:bg-green-800 text-white"
                    onClick={onSendAnyway}
                    disabled={isPending}
                >
                    {isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Send anyway
                </Button>
            </div>
        </>
    );
}
